import type { ICommentAttachment, ICommentLink } from '@atlas/types'
import { CardShell, FileThumbnail, ListSkeleton } from '@atlas/ui-components'
import { Avatar, Badge, cn } from '@atlas/ui-primitives'
import { ExternalLink, ImageOff, MessageSquare, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { ConfirmDialog } from '@/components/confirm/ConfirmDialog'
import type { TimelineEntry } from '@/domain/work-order-timeline'
import { formatShortDateTime } from '@/lib/format-short-date-time'
import { initials } from '@/lib/labels'
import { REFUSAL, Restricted } from '@/lib/refusal'
import type { CommentInput } from '@/services/comment.service'
import { CommentComposer } from './CommentComposer'
import { ImageLightbox } from './ImageLightbox'

/**
 * A timeline entry with its author/actor name resolved for display. `who` is
 * the resolved display name for the comment author / activity actor ("System"
 * when none). Intersection (not `extends`) so the `kind` discriminant survives.
 */
export type DisplayEntry = TimelineEntry & { who: string }

export type CommentEntry = Extract<DisplayEntry, { kind: 'comment' }>

/** A reference image as the thread can show it; no `url` until its thumbnail exists. */
export interface CommentImage {
	name: string
	url?: string | null
}

export interface WorkOrderCommentsProps {
	comments: CommentEntry[]
	isLoading?: boolean
	/** Hidden when the caller can't post. */
	canComment?: boolean
	/** The composer keeps its draft until this resolves, and for a retry if it rejects. */
	onComment?: (input: CommentInput) => Promise<unknown> | undefined
	/** Called once the user confirms; the dialog stays open, busy, until this settles. */
	onDeleteComment?: (id: string) => Promise<unknown> | undefined
	/**
	 * Whether this comment's delete control is offered. Omitting it withholds
	 * delete from every row: a caller has to say who may, because a permission
	 * question nobody asked must not answer itself as yes.
	 */
	canDeleteComment?: (entry: CommentEntry) => boolean
	isPosting?: boolean
	/** Uploads one reference image and resolves with its `fileId`. Absent hides the composer's attach action. */
	onUploadImage?: (file: File) => Promise<string>
	/** Deletes an uploaded image the composer let go of before posting it. */
	onDiscardImage?: (fileId: string) => void
	/** Resolves an attachment to what can be shown; unknown files render as a placeholder tile. */
	imageOf?: (fileId: string) => CommentImage | undefined
	/** Resolves an attachment's original; the lightbox shows the thumbnail until it arrives. */
	fullSizeOf?: (fileId: string) => Promise<string>
}

interface OpenImage {
	fileId: string
	src: string
	name: string
}

/** How far a reply is indented under the note it answers; deeper replies stop indenting here. */
const DEPTH_INDENT = ['', 'ml-8', 'ml-16'] as const

/** Empty / loading placeholder for a thread or tab pane. */
export function Placeholder({ loading, empty }: { loading?: boolean; empty: string }) {
	if (loading) return <ListSkeleton label="Loading…" />
	return (
		<p className="flex items-center gap-2 text-sm text-ds-text-muted">
			<MessageSquare className="h-4 w-4" /> {empty}
		</p>
	)
}

/** A comment's reference images; each one with a thumbnail opens full size. */
function Attachments({
	attachments,
	imageOf,
	onOpen,
}: {
	attachments: ICommentAttachment[]
	imageOf?: WorkOrderCommentsProps['imageOf']
	onOpen: (image: OpenImage) => void
}) {
	return (
		<ul aria-label="Reference images" className="mt-2 flex flex-wrap gap-2">
			{attachments.map(({ fileId }) => {
				const image = imageOf?.(fileId)
				const name = image?.name ?? 'Reference image'
				const src = image?.url
				return (
					<li key={fileId}>
						{src ? (
							<button type="button" aria-label={`Open ${name}`} onClick={() => onOpen({ fileId, src, name })}>
								<FileThumbnail name={name} thumbnailUrl={src} size="md" />
							</button>
						) : (
							<FileThumbnail name={name} size="md" icon={ImageOff} />
						)}
					</li>
				)
			})}
		</ul>
	)
}

function Links({ links }: { links: ICommentLink[] }) {
	return (
		<ul aria-label="Links" className="mt-2 flex flex-col gap-1">
			{links.map((link, i) => (
				<li key={`${link.url}-${i}`} className="min-w-0">
					<a
						href={link.url}
						target="_blank"
						rel="noopener noreferrer"
						title={link.url}
						className="inline-flex max-w-full items-center gap-1 text-sm text-ds-primary hover:underline"
					>
						<ExternalLink className="h-3.5 w-3.5 shrink-0" />
						<span className="truncate">{link.label ?? link.url}</span>
					</a>
				</li>
			))}
		</ul>
	)
}

/**
 * One comment row — avatar, author, time, body, attachments, and a delete affordance.
 *
 * `canDelete` is per row rather than per thread: deletion is the one action
 * scoped by ownership, so the same reader may remove their own comment and not
 * the one above it. A row they may not remove keeps the control, dimmed and
 * explaining itself, so the rule is learnable from the screen.
 *
 * A comment from Workfront is read-only: it offers no delete at all, since no
 * one in ATLAS may remove it, and carries a chip saying where it came from.
 */
function CommentRow({
	e,
	onDelete,
	canDelete = true,
	imageOf,
	onOpenImage,
}: {
	e: CommentEntry
	onDelete?: (id: string) => void
	canDelete?: boolean
	imageOf?: WorkOrderCommentsProps['imageOf']
	onOpenImage: (image: OpenImage) => void
}) {
	const indent = DEPTH_INDENT[Math.min(e.depth ?? 0, DEPTH_INDENT.length - 1)]
	return (
		<li className={cn('flex gap-3', indent)}>
			<Avatar size="sm" fallback={initials(e.who)} alt={e.who} />
			<CardShell className="min-w-0 flex-1 p-0 px-4 py-3">
				<div className="mb-1 flex items-center justify-between">
					<span className="inline-flex items-center gap-2 text-sm font-medium text-ds-text">
						{e.who}
						{e.external ? (
							<Badge size="sm" variant="muted">
								From Workfront
							</Badge>
						) : null}
						<span className="font-normal text-ds-text-subtle">{formatShortDateTime(e.at)}</span>
					</span>
					{onDelete && !e.external ? (
						<Restricted allowed={canDelete} why={REFUSAL.deleteComment}>
							<button
								type="button"
								aria-label="Delete comment"
								onClick={() => onDelete(e.id)}
								className="text-ds-text-subtle hover:text-ds-danger-strong"
							>
								<Trash2 className="h-3.5 w-3.5" />
							</button>
						</Restricted>
					) : null}
				</div>
				{e.content ? <p className="whitespace-pre-wrap break-words text-sm text-ds-text">{e.content}</p> : null}
				{e.attachments?.length ? (
					<Attachments attachments={e.attachments} imageOf={imageOf} onOpen={onOpenImage} />
				) : null}
				{e.links?.length ? <Links links={e.links} /> : null}
			</CardShell>
		</li>
	)
}

/**
 * A work order's comment thread: newest-last with the composer anchored at the
 * bottom (chat-style). Presentational — the host resolves author names and
 * images, and wires the mutations and the image upload.
 */
export function WorkOrderComments({
	comments,
	isLoading,
	canComment = false,
	onComment,
	onDeleteComment,
	canDeleteComment,
	isPosting,
	onUploadImage,
	onDiscardImage,
	imageOf,
	fullSizeOf,
}: WorkOrderCommentsProps) {
	const [preview, setPreview] = useState<OpenImage | null>(null)
	const [confirmingId, setConfirmingId] = useState<string | null>(null)
	const [deleting, setDeleting] = useState(false)
	const confirmDelete = () => {
		if (!confirmingId || !onDeleteComment) return
		setDeleting(true)
		// A failure is reported by the mutation; the dialog stays open for a retry.
		Promise.resolve(onDeleteComment(confirmingId))
			.then(
				() => setConfirmingId(null),
				() => undefined,
			)
			.finally(() => setDeleting(false))
	}
	const openImage = (image: OpenImage) => {
		setPreview(image)
		fullSizeOf?.(image.fileId).then(
			(src) => setPreview((current) => (current?.fileId === image.fileId ? { ...current, src } : current)),
			// The thumbnail stays up: a smaller preview beats none.
			() => undefined,
		)
	}
	return (
		<>
			{isLoading ? (
				<Placeholder loading empty="" />
			) : comments.length === 0 ? (
				<Placeholder empty="No comments yet." />
			) : (
				<ul className="space-y-3">
					{comments.map((e) => (
						<CommentRow
							key={e.id}
							e={e}
							onDelete={onDeleteComment ? setConfirmingId : undefined}
							canDelete={canDeleteComment?.(e) ?? false}
							imageOf={imageOf}
							onOpenImage={openImage}
						/>
					))}
				</ul>
			)}
			{canComment && onComment ? (
				<CommentComposer
					onComment={onComment}
					isPosting={isPosting}
					onUploadImage={onUploadImage}
					onDiscardImage={onDiscardImage}
				/>
			) : null}
			<ConfirmDialog
				open={confirmingId !== null}
				title="Delete this comment?"
				description="The comment is removed for everyone, along with any images it carries. This cannot be undone."
				confirmLabel="Delete"
				confirmVariant="danger"
				busyLabel="Deleting…"
				submitting={deleting}
				onConfirm={confirmDelete}
				onCancel={() => setConfirmingId(null)}
			/>
			{preview ? <ImageLightbox isOpen src={preview.src} name={preview.name} onClose={() => setPreview(null)} /> : null}
		</>
	)
}
